import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { fetchWorkers } from '../../services/workerService';

interface DocumentsTabProps {
  farmId: string;
}

interface WorkerDocument {
  id: string;
  worker_id: string;
  document_type: string;
  document_name: string;
  file_url?: string;
  upload_date: string;
  status: string;
  worker_name?: string;
}

interface Worker {
  id: string;
  name: string;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ farmId }) => {
  const [documents, setDocuments] = useState<WorkerDocument[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    worker_id: '',
    document_type: '',
    document_name: '',
    file: null as File | null,
    status: 'pending'
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch workers for this farm
      const workersData = await fetchWorkers(farmId);
      setWorkers(workersData);

      // Fetch documents for this farm
      const { data: documentsData, error: documentsError } = await supabase
        .from('worker_documents')
        .select(`
          *,
          workers!inner(name, farm_id)
        `)
        .eq('workers.farm_id', farmId)
        .order('upload_date', { ascending: false });

      if (documentsError) throw documentsError;

      const formattedDocuments = documentsData?.map(doc => ({
        ...doc,
        worker_name: doc.workers?.name || 'Unknown'
      })) || [];

      setDocuments(formattedDocuments);
    } catch (err) {
      console.error('Error loading documents data:', err);
      setError('Failed to load documents data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async () => {
    try {
      setError(null);
      
      let fileUrl = null;
      
      // Upload file if provided
      if (formData.file) {
        const fileExt = formData.file.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `worker-documents/${farmId}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, formData.file);
          
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('documents')
          .getPublicUrl(filePath);
          
        fileUrl = publicUrl;
      }
      
      const { error } = await supabase
        .from('worker_documents')
        .insert([{
          worker_id: formData.worker_id,
          document_type: formData.document_type,
          document_name: formData.document_name,
          file_url: fileUrl,
          status: formData.status,
          farm_id: farmId,
          upload_date: new Date().toISOString()
        }]);

      if (error) throw error;

      setDialogOpen(false);
      setFormData({
        worker_id: '',
        document_type: '',
        document_name: '',
        file: null,
        status: 'pending'
      });
      loadData();
    } catch (err) {
      console.error('Error adding document:', err);
      setError('Failed to add document');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Worker Documents</Typography>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Add Document
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Document Name</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((document) => (
              <TableRow key={document.id}>
                <TableCell>{document.worker_name}</TableCell>
                <TableCell>{document.document_type}</TableCell>
                <TableCell>{document.document_name}</TableCell>
                <TableCell>{new Date(document.upload_date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip 
                    label={document.status} 
                    color={getStatusColor(document.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {document.file_url && (
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => window.open(document.file_url, '_blank')}
                    >
                      View
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Worker Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={formData.worker_id}
              onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })}
              margin="normal"
              required
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>
                  {worker.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Document Type"
              fullWidth
              value={formData.document_type}
              onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
              margin="normal"
              required
            >
              <MenuItem value="id_card">ID Card</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="certificate">Certificate</MenuItem>
              <MenuItem value="medical">Medical Certificate</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            <TextField
              label="Document Name"
              fullWidth
              value={formData.document_name}
              onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              type="file"
              fullWidth
              onChange={(e) => {
                const target = e.target as HTMLInputElement;
                setFormData({ ...formData, file: target.files?.[0] || null });
              }}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              label="Upload File"
            />
            <TextField
              select
              label="Status"
              fullWidth
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              margin="normal"
              required
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddDocument} variant="contained">
            Add Document
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsTab;